import { create } from 'zustand'
import type { S3Object, ClientConfig, RootGroupEntry } from '@/shared/api/generated'
import { api, getErrorMessage } from '@/shared/api/generated'
import { filesApi } from '../api/files'
import { toast } from '@/shared/stores/toastStore'

export interface FileBrowserState {
  // Navigation
  currentPath: string
  currentGroupId: number
  currentViewId: number
  currentGroupName: string
  currentGroupRole: string
  currentGroupPerms: number
  currentFolderViewId: number
  currentRegistryEnabled: boolean

  // Tree (folders only, keyed by path)
  treeChildren: Record<string, S3Object[]>
  treeHasMore: Record<string, boolean>
  expandedFolders: Set<string>
  treeLoading: Set<string>

  // File list (right panel)
  files: S3Object[]
  filesLoading: boolean
  hasMore: boolean
  total: number

  // Selection
  selectedIndex: number
  checkedItems: Map<string, S3Object>

  // Sort
  sortColumn: 'name' | 'size' | 'modified'
  sortDirection: 'asc' | 'desc'

  // Config
  config: ClientConfig | null

  // Root group context (keyed by mount key, e.g. "/testgroup/")
  rootGroups: Map<string, RootGroupEntry>

  // Actions
  navigateTo: (path: string, groupId?: number, groupName?: string, groupRole?: string, groupPerms?: number, viewId?: number, registryEnabled?: boolean) => Promise<void>
  detectFolderView: () => Promise<void>
  setSort: (column: 'name' | 'size' | 'modified') => void
  navigateUp: () => Promise<void>
  toggleExpand: (path: string, obj: S3Object) => Promise<void>
  loadMore: () => Promise<void>
  loadMoreTree: (path: string, obj: S3Object) => Promise<void>
  refresh: () => Promise<void>
  setSelectedIndex: (i: number) => void
  toggleCheck: (obj: S3Object) => void
  clearChecked: () => void
  loadConfig: () => Promise<void>
}

export const useFileBrowserStore = create<FileBrowserState>()((set, get) => ({
  currentPath: '/',
  currentGroupId: 0,
  currentViewId: 0,
  currentGroupName: '',
  currentGroupRole: '',
  currentGroupPerms: 0,
  currentFolderViewId: 0,
  currentRegistryEnabled: false,

  treeChildren: {},
  treeHasMore: {},
  expandedFolders: new Set<string>(),
  treeLoading: new Set<string>(),

  files: [],
  filesLoading: true,
  hasMore: false,
  total: 0,

  selectedIndex: -1,
  checkedItems: new Map(),

  sortColumn: 'name',
  sortDirection: 'asc',

  config: null,

  rootGroups: new Map(),

  navigateTo: async (path, groupId, groupName, groupRole, groupPerms, viewId, registryEnabled) => {
    const s = get()
    // Normalize: ensure leading /
    const normalPath = path === '/' ? '/' : '/' + path.replace(/^\//, '')
    const gid = groupId ?? s.currentGroupId
    const gname = groupName ?? s.currentGroupName
    const grole = groupRole ?? s.currentGroupRole
    const gperms = groupPerms ?? s.currentGroupPerms
    const vid = viewId ?? s.currentViewId
    const regEnabled = registryEnabled ?? s.currentRegistryEnabled

    set({
      currentPath: normalPath,
      currentGroupId: gid,
      currentViewId: vid,
      currentGroupName: gname,
      currentGroupRole: grole,
      currentGroupPerms: gperms,
      currentFolderViewId: 0,
      currentRegistryEnabled: regEnabled,
      selectedIndex: -1,
      checkedItems: new Map(),
    })

    try {
      const isRoot = normalPath === '/'
      const resp = await filesApi.list(normalPath, isRoot ? 0 : gid)
      // Filter out self-referencing folder marker (folder listing includes itself)
      const files = (resp.files ?? []).filter(f => f.key !== normalPath && f.key !== normalPath.replace(/^\//, ''))
      const update: Partial<FileBrowserState> = { files, hasMore: resp.has_more, total: resp.total, filesLoading: false }
      if (isRoot && resp.groups) {
        // Build root groups map keyed by mount key (e.g. "/testgroup/")
        const rg = new Map<string, RootGroupEntry>()
        for (const g of resp.groups) rg.set('/' + g.mount_name + '/', g)
        update.rootGroups = rg
      } else if (!isRoot && resp.group_id) {
        // Non-root: use server-authoritative group context from response envelope
        update.currentGroupId = resp.group_id
        update.currentGroupName = resp.group_name ?? gname
        update.currentGroupRole = resp.group_role ?? grole
        update.currentGroupPerms = resp.group_perms ?? gperms
        update.currentViewId = resp.view_id ?? vid
        update.currentRegistryEnabled = resp.registry_enabled ?? regEnabled
      }
      set(update)
    } catch (err) {
      toast.error(getErrorMessage(err))
      set({ files: [], hasMore: false, total: 0, filesLoading: false })
    }

    // Detect if current folder has a view on it (for toolbar buttons)
    await get().detectFolderView()

    // Auto-expand tree ancestors so the tree reflects the current path
    if (normalPath !== '/') {
      const parts = normalPath.replace(/\/$/, '').split('/').filter(Boolean)
      const { expandedFolders, treeChildren } = get()
      const next = new Set(expandedFolders)
      let accumulated = '/'
      for (let i = 0; i < parts.length; i++) {
        accumulated = '/' + parts.slice(0, i + 1).join('/') + '/'
        if (!next.has(accumulated)) {
          next.add(accumulated)
          // Load children if not cached
          if (!treeChildren[accumulated]) {
            try {
              const treeResp = await filesApi.list(accumulated, gid)
              const folders = (treeResp.files ?? []).filter(f => f.is_dir && f.key !== accumulated)
              set(s => ({
                treeChildren: { ...s.treeChildren, [accumulated]: folders },
                treeHasMore: { ...s.treeHasMore, [accumulated]: treeResp.has_more },
              }))
            } catch (err) { toast.error(getErrorMessage(err)) }
          }
        }
      }
      set({ expandedFolders: next })
    }
  },

  detectFolderView: async () => {
    const { currentPath, currentGroupId, currentGroupRole } = get()
    if (currentPath === '/' || !currentGroupId || currentGroupRole !== 'admin') {
      set({ currentFolderViewId: 0 })
      return
    }
    // Relative path within group: strip leading /groupname/
    const parts = currentPath.replace(/^\//, '').replace(/\/$/, '').split('/')
    if (parts.length < 2) { set({ currentFolderViewId: 0 }); return }
    const relPath = parts.slice(1).join('/') + '/'
    try {
      const views = await api.listGroupViews(currentGroupId)
      const match = views.find(v => relPath === v.base_path || relPath === v.base_path + '/')
      set({ currentFolderViewId: match?.id ?? 0 })
    } catch (err) {
      toast.error(getErrorMessage(err))
      set({ currentFolderViewId: 0 })
    }
  },

  navigateUp: async () => {
    const { currentPath, navigateTo } = get()
    if (currentPath === '/') return
    const parts = currentPath.replace(/\/$/, '').split('/').filter(Boolean)
    if (parts.length <= 1) {
      set({ currentGroupId: 0, currentViewId: 0, currentGroupName: '', currentGroupRole: '', currentGroupPerms: 0, currentFolderViewId: 0, currentRegistryEnabled: false })
      await navigateTo('/')
    } else {
      parts.pop()
      await navigateTo('/' + parts.join('/') + '/')
    }
  },

  toggleExpand: async (path, _obj) => {
    const { expandedFolders, treeChildren, treeLoading } = get()
    const next = new Set(expandedFolders)
    if (next.has(path)) {
      next.delete(path)
      set({ expandedFolders: next })
      return
    }
    next.add(path)
    set({ expandedFolders: next })

    // Load children if not cached
    if (!treeChildren[path]) {
      const loading = new Set(treeLoading)
      loading.add(path)
      set({ treeLoading: loading })
      try {
        const gid = get().rootGroups.get(path)?.id || get().currentGroupId
        const resp = await filesApi.list(path, path === '/' ? 0 : gid)
        const folders = (resp.files ?? []).filter(f => f.is_dir && f.key !== path)
        // At root, API already returns only user's groups — no client filter needed
        const filtered = folders
        set(s => ({
          treeChildren: { ...s.treeChildren, [path]: filtered },
          treeHasMore: { ...s.treeHasMore, [path]: resp.has_more },
          treeLoading: (() => { const l = new Set(s.treeLoading); l.delete(path); return l })(),
        }))
      } catch (err) {
        toast.error(getErrorMessage(err))
        const loading2 = new Set(get().treeLoading)
        loading2.delete(path)
        set({ treeLoading: loading2 })
      }
    }
  },

  loadMoreTree: async (path, _obj) => {
    const { treeChildren } = get()
    const existing = treeChildren[path] || []
    try {
      const gid = get().rootGroups.get(path)?.id || get().currentGroupId
      const resp = await filesApi.list(path, path === '/' ? 0 : gid, existing.length)
      const folders = (resp.files ?? []).filter(f => f.is_dir && f.key !== path)
      set(s => ({
        treeChildren: { ...s.treeChildren, [path]: [...existing, ...folders] },
        treeHasMore: { ...s.treeHasMore, [path]: resp.has_more },
      }))
    } catch (err) { toast.error(getErrorMessage(err)) }
  },

  loadMore: async () => {
    const { currentPath, currentGroupId, files } = get()
    const isRoot = currentPath === '/'
    try {
      const resp = await filesApi.list(currentPath, isRoot ? 0 : currentGroupId, files.length)
      set(s => ({ files: [...s.files, ...(resp.files ?? [])], hasMore: resp.has_more, total: resp.total }))
    } catch (err) { toast.error(getErrorMessage(err)) }
  },

  refresh: async () => {
    const s = get()
    const newTree = { ...s.treeChildren }
    const newTreeMore = { ...s.treeHasMore }
    const invalidated: string[] = []

    // Always invalidate root (structural changes: new groups, views)
    delete newTree['/']
    delete newTreeMore['/']
    if (s.expandedFolders.has('/')) invalidated.push('/')

    // Current path in both formats (tree keys may lack leading /)
    const paths = [s.currentPath, s.currentPath.replace(/^\//, '')]
    for (const p of paths) {
      delete newTree[p]
      delete newTreeMore[p]
      if (s.expandedFolders.has(p)) invalidated.push(p)
    }
    // Parent path in both formats
    const parentPath = s.currentPath.replace(/[^/]+\/$/, '')
    if (parentPath && parentPath !== s.currentPath) {
      const parentPaths = [parentPath, parentPath.replace(/^\//, '')]
      for (const p of parentPaths) {
        delete newTree[p]
        delete newTreeMore[p]
        if (s.expandedFolders.has(p)) invalidated.push(p)
      }
    }
    set({ treeChildren: newTree, treeHasMore: newTreeMore })
    // Re-fetch file list
    await s.navigateTo(s.currentPath)
    // Re-fetch tree data for expanded folders that were invalidated
    for (const path of invalidated) {
      const gid = s.currentGroupId
      try {
        const resp = await filesApi.list(path.startsWith('/') ? path : '/' + path, path === '/' ? 0 : gid)
        const folders = (resp.files ?? []).filter(f => f.is_dir && f.key !== path && f.key !== '/' + path)
        set(st => ({
          treeChildren: { ...st.treeChildren, [path]: folders },
          treeHasMore: { ...st.treeHasMore, [path]: resp.has_more },
        }))
      } catch (err) { toast.error(getErrorMessage(err)) }
    }
  },

  setSelectedIndex: (i) => set({ selectedIndex: i }),

  toggleCheck: (obj) => set(s => {
    const next = new Map(s.checkedItems)
    if (next.has(obj.key)) next.delete(obj.key)
    else next.set(obj.key, obj)
    return { checkedItems: next }
  }),

  clearChecked: () => set({ checkedItems: new Map() }),

  setSort: (column) => set(s => ({
    sortColumn: column,
    sortDirection: s.sortColumn === column && s.sortDirection === 'asc' ? 'desc' : 'asc',
    selectedIndex: -1,
  })),

  loadConfig: async () => {
    try {
      const config = await filesApi.getConfig()
      set({ config })
    } catch (err) { toast.error(getErrorMessage(err)) }
  },
}))
